import type { CompanyResolutionParams } from "@/lib/company-resolution";

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SIRET_PATTERN = /^\d{14}$/;
const SIREN_PATTERN = /^\d{9}$/;

/** SIREN of La Poste, whose establishments break the rule every other siret follows. */
const LA_POSTE_SIREN = "356000000";

/** Whether a string of digits ends on a valid Luhn check digit. */
function passesLuhn(digits: string): boolean {
  let sum = 0;
  for (let index = 0; index < digits.length; index++) {
    let digit = Number(digits[digits.length - 1 - index]);
    if (index % 2 === 1) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  return sum % 10 === 0;
}

/**
 * A siret with a valid check digit. A number the model made up misses Luhn 9
 * times in 10, which is what made a hallucinated siret a chip that opened
 * nothing. La Poste is the known exception: its establishments are valid when
 * their digits add up to a multiple of 5, and fail Luhn — measured on
 * 2026-09-29, 0 of 5 post offices passed Luhn and 5 of 5 that rule — while its
 * head office passes Luhn like any other.
 */
function isValidSiret(digits: string): boolean {
  if (!SIRET_PATTERN.test(digits)) return false;
  if (!digits.startsWith(LA_POSTE_SIREN)) return passesLuhn(digits);
  const digitSum = [...digits].reduce((total, digit) => total + Number(digit), 0);
  return passesLuhn(digits) || digitSum % 5 === 0;
}

/** A siren with a valid Luhn check digit — La Poste's included. */
function isValidSiren(digits: string): boolean {
  return SIREN_PATTERN.test(digits) && passesLuhn(digits);
}
/** Two labels or more, letters/digits/hyphens only — no scheme, port or path. */
const BARE_DOMAIN_PATTERN =
  /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/;

/**
 * Directory, social and marketplace domains that identify a listing rather than
 * a company. Mirrors `PUBLIC_PROFILE_DOMAINS` in
 * `api/src/common/utils/utils.ts`; kept duplicated because `@shaarp/common`
 * ships types only, the same reason `company-name-matching.ts` is duplicated.
 * Keep the two lists in step.
 */
const DIRECTORY_DOMAINS: readonly string[] = [
  "facebook.com",
  "instagram.com",
  "twitter.com",
  "x.com",
  "linkedin.com",
  "tiktok.com",
  "youtube.com",
  "pinterest.com",
  "snapchat.com",
  "threads.net",
  "planity.com",
  "doctolib.fr",
  "booking.com",
  "airbnb.com",
  "airbnb.fr",
  "reservio.com",
  "treatwell.fr",
  "treatwell.com",
  "yelp.com",
  "yelp.fr",
  "tripadvisor.com",
  "tripadvisor.fr",
  "pagesjaunes.fr",
  "trustpilot.com",
  "glassdoor.com",
  "glassdoor.fr",
  "amazon.com",
  "amazon.fr",
  "ebay.com",
  "ebay.fr",
  "etsy.com",
  "leboncoin.fr",
  "github.com",
  "medium.com",
  "wordpress.com",
  "blogspot.com",
  "tumblr.com",
  "linktr.ee",
  "bit.ly",
] as const;

/**
 * A LinkedIn *company* page URL, and nothing else. Mirrors
 * `LINKEDIN_COMPANY_URL_PATTERN` in
 * `api/src/common/utils/company-mention-markers.ts`: a bare handle is refused
 * on both sides, because one rebuilt from the company's name is exactly as
 * plausible as the real one and only the URL proves a tool returned it.
 */
const LINKEDIN_COMPANY_URL_PATTERN =
  /^(?:https?:\/\/)?(?:[a-z0-9-]+\.)?linkedin\.com\/company\/([a-z0-9\-_.%]{2,})\/?$/i;

/**
 * The handle of a LinkedIn company URL, lowercased, or undefined when the value
 * is not one. Query string and fragment are dropped first — a URL copied out of
 * a search result routinely carries `?trk=...`.
 */
export function linkedinCompanyHandle(
  value: string | undefined
): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  const withoutParams = trimmed.split(/[?#]/)[0];
  const handle = withoutParams.match(LINKEDIN_COMPANY_URL_PATTERN)?.[1];
  return handle?.toLowerCase();
}

/**
 * The bare domain of a `domain` value, lowercased, or undefined when it has no
 * host at all. The prompt asks for "exemple.com", but a model copying the link
 * it displays writes `https://`, `www.` or a path: read raw, that was plain
 * text for a company Shaarp could open, and two resolution keys for one
 * company. Mirrors `bareDomainOf` in
 * `api/src/common/utils/company-mention-markers.ts`.
 */
export function bareDomainOf(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  try {
    const withScheme = /^https?:\/\//i.test(trimmed)
      ? trimmed
      : `https://${trimmed}`;
    const host = new URL(withScheme).hostname.replace(/^www\./, "");
    return host || undefined;
  } catch {
    return undefined;
  }
}

/**
 * The host of a website the web profile states, lowercased, or null unless the
 * value is a bare domain — a scheme, a `www.` and a trailing slash tolerated,
 * nothing else. The value is model text the API keeps as written: read through
 * {@link bareDomainOf} alone, `acme.io@evil.com` is a link to `evil.com` that
 * a label built from the text would call `acme.io`. What is shown, linked or
 * searched is this host, never the text.
 */
export function websiteDomainOf(
  value: string | null | undefined
): string | null {
  const trimmed = value?.trim().toLowerCase();
  if (!trimmed) return null;
  const host = bareDomainOf(trimmed);
  if (!host || !BARE_DOMAIN_PATTERN.test(host)) return null;
  const written = trimmed
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/$/, "");
  return written === host ? host : null;
}

/** Trimmed value, or undefined when absent or blank. */
function cleaned(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : undefined;
}

/**
 * A registry number is only usable once its separators are removed — spaces
 * (regular and non-breaking), dots and hyphens are all forms the model
 * routinely emits for the same siret/siren.
 */
function digitsOf(value: string): string {
  return value.replace(/[\s.-]/g, "");
}

/** Why a domain identifies a company, or why it was refused. */
function checkDomain(
  domain: string
): "valid" | "noHost" | "badFormat" | "directory" {
  const bareDomain = bareDomainOf(domain);
  if (!bareDomain) return "noHost";
  const normalized = bareDomain.toLowerCase();
  if (!BARE_DOMAIN_PATTERN.test(normalized)) return "badFormat";
  // Directory, social and marketplace domains identify a listing, not a
  // company, and the assistant routinely attaches one of them to several
  // distinct companies.
  const isDirectory = DIRECTORY_DOMAINS.some(
    (blocked) => normalized === blocked || normalized.endsWith(`.${blocked}`)
  );
  return isDirectory ? "directory" : "valid";
}

/** A registry number's verdict: 9 or 14 digits, then its check digit. */
function checkRegistryNumber(
  value: string,
  pattern: RegExp,
  isValid: (digits: string) => boolean
): "valid" | "badFormat" | "badChecksum" {
  const digits = digitsOf(value);
  if (!pattern.test(digits)) return "badFormat";
  return isValid(digits) ? "valid" : "badChecksum";
}

export type CompanyIdentifierCheck =
  | "valid"
  | "notUuid"
  | "badFormat"
  | "badChecksum"
  | "noHost"
  | "directory"
  | "notCompanyPage";

export type CompanyIdentifierChecks = Partial<
  Record<
    "id" | "siret" | "siren" | "domain" | "linkedin",
    CompanyIdentifierCheck
  >
>;

/**
 * The verdict on each identifier a company mention carries: `"valid"` when it
 * can open a profile, otherwise the reason it was refused. Absent or blank
 * values are left out. The single source of `hasReliableCompanyIdentifier`, so
 * the admin debug card shows exactly what the chip decided on.
 */
export function companyIdentifierChecks({
  id,
  siret,
  siren,
  domain,
  linkedin,
}: CompanyResolutionParams): CompanyIdentifierChecks {
  const checks: CompanyIdentifierChecks = {};

  const cleanedId = cleaned(id);
  if (cleanedId) checks.id = UUID_PATTERN.test(cleanedId) ? "valid" : "notUuid";

  const cleanedSiret = cleaned(siret);
  if (cleanedSiret) {
    checks.siret = checkRegistryNumber(cleanedSiret, SIRET_PATTERN, isValidSiret);
  }

  const cleanedSiren = cleaned(siren);
  if (cleanedSiren) {
    checks.siren = checkRegistryNumber(cleanedSiren, SIREN_PATTERN, isValidSiren);
  }

  const cleanedDomain = cleaned(domain);
  if (cleanedDomain) checks.domain = checkDomain(cleanedDomain);

  // The weakest of the five: the page opens the head office like a domain
  // does. `linkedin.com` stays blocked as a `domain` value — the page is the
  // company's profile, never its site.
  const cleanedLinkedin = cleaned(linkedin);
  if (cleanedLinkedin) {
    checks.linkedin = linkedinCompanyHandle(cleanedLinkedin)
      ? "valid"
      : "notCompanyPage";
  }

  return checks;
}

/**
 * Whether a company mention carries an identifier we can actually open a
 * profile with. Presence is not enough: the marker is model output, and a
 * hallucinated `siret="123"` would produce exactly the dead click this rule
 * exists to remove. Mirrored on the API by
 * `api/src/common/utils/company-mention-reliability.ts`.
 */
export function hasReliableCompanyIdentifier(
  params: CompanyResolutionParams
): boolean {
  return Object.values(companyIdentifierChecks(params)).includes("valid");
}
